import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import FilterButtonMenu from './FilterButtonMenu';
import ListingFilters from './ListingFilters';
import { FilterVal } from 'src/interfaces/iproptypes';
import { COLOR_PRIMARY } from 'src/utils/const';
import {
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_1_200,
  COLOR_PRIMARY_2,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';
import PerformanceFilter from './PerformanceFilter';
import AmenitiesFilter from './AmenitiesFilter';
import { useCanAccessExpertPlan } from 'src/hooks/useFeatureAccess';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
interface FilterBarProps {
  isMobileView: boolean;
  dashboardLayoutView: string;
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterVal: FilterVal;
  priceTiers: any;
  currency: string;
  resetFilter: (filterNamesToReset: string[]) => void;
  setFilterApplied: React.Dispatch<React.SetStateAction<boolean>>;
  revenuePotentialTiersDistribution: any;
  handleOnSampleClick: (plan: string) => void;
  showFilterComingSoonAlert: boolean | 'open';
  isUserAllowed: boolean;
  FilterCoingSoonComponent: React.ReactNode | null;
  filtersToRender?: ('listing' | 'performance' | 'amenities')[];
}

const FilterBar: React.FC<FilterBarProps> = ({
  isMobileView,
  dashboardLayoutView,
  setFilterVal,
  filterVal,
  priceTiers,
  currency,
  resetFilter,
  setFilterApplied,
  revenuePotentialTiersDistribution,
  handleOnSampleClick,
  showFilterComingSoonAlert,
  isUserAllowed,
  FilterCoingSoonComponent,
  filtersToRender = ['listing', 'performance', 'amenities'],
}) => {
  const buttonStyles = {
    backgroundColor: isMobileView ? 'white' : COLOR_PRIMARY + '22',
    color: 'black',
    marginRight: 1,
    '&:hover': {
      backgroundColor: COLOR_PRIMARY + '44',
    },
    textTransform: 'none',
    borderRadius: '20px',
    ...(isMobileView ? { paddingRight: '12px' } : { padding: '4px 12px' }),
  };

  const menuPaperProps = {
    style: {
      minWidth: '200px',
      marginTop: '8px',
    },
  };

  const menuItemStyle = {
    padding: '10px 16px',
  };

  const [totalListingFilterApplied, setTotalListingFilterApplied] = useState<number>(0);
  const [totalPerformanceFilterApplied, setTotalPerformanceFilterApplied] = useState<number>(0);
  const [totalAmenitiesFilterApplied, setTotalAmenitiesFilterApplied] = useState<number>(0);

  const upgradeMessage = useUpgradeMesageExpertFeature();

  useEffect(() => {
    if (
      totalListingFilterApplied > 0 ||
      totalPerformanceFilterApplied > 0 ||
      totalAmenitiesFilterApplied > 0
    ) {
      setFilterApplied(true);
    } else {
      setFilterApplied(false);
    }
  }, [totalListingFilterApplied, totalPerformanceFilterApplied, totalAmenitiesFilterApplied]);

  const UpgradeCTA = (closeMenu: () => void) => {
    return (
      <Grid item xs={12} sx={{ mb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
          }}
          onClick={() => {
            handleOnSampleClick('expert');
            closeMenu();
          }}
        >
          {upgradeMessage}
        </Typography>
      </Grid>
    );
  };

  const ListingFilterComponent = (handleCloseMenu: () => void) => (
    <ListingFilters
      setFilterVal={setFilterVal}
      filterVal={filterVal}
      isMobileView={isMobileView}
      setTotalFilterApplied={setTotalListingFilterApplied}
      handleCloseMenu={handleCloseMenu}
      resetFilter={resetFilter}
      isUserAllowed={isUserAllowed}
      handleOnSampleClick={handleOnSampleClick}
      UpgradeCTA={UpgradeCTA}
      FilterCoingSoonComponent={FilterCoingSoonComponent}
    />
  );

  const PerformanceFilterComponent = (handleCloseMenu: () => void) => (
    <PerformanceFilter
      setFilterVal={setFilterVal}
      filterVal={filterVal}
      isMobileView={isMobileView}
      priceTiers={priceTiers}
      currency={currency}
      setTotalFilterApplied={setTotalPerformanceFilterApplied}
      handleCloseMenu={handleCloseMenu}
      resetFilter={resetFilter}
      revenuePotentialTiersDistribution={revenuePotentialTiersDistribution}
      isUserAllowed={isUserAllowed}
      handleOnSampleClick={handleOnSampleClick}
      UpgradeCTA={UpgradeCTA}
      FilterCoingSoonComponent={FilterCoingSoonComponent}
    />
  );

  const AmenitiesFilterComponent = (handleCloseMenu: () => void) => (
    <AmenitiesFilter
      setFilterVal={setFilterVal}
      filterVal={filterVal}
      isMobileView={isMobileView}
      setTotalFilterApplied={setTotalAmenitiesFilterApplied}
      handleCloseMenu={handleCloseMenu}
      resetFilter={resetFilter}
      isUserAllowed={isUserAllowed}
      handleOnSampleClick={handleOnSampleClick}
      UpgradeCTA={UpgradeCTA}
      FilterCoingSoonComponent={FilterCoingSoonComponent}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 0,
        py: 0,
      }}
    >
      {!isMobileView && (
        <Typography
          variant="body1"
          textTransform="uppercase"
          sx={{ marginRight: 1, fontWeight: 'bold', color: COLOR_TEXT_GREY }}
        >
          Filters:
        </Typography>
      )}
      {filtersToRender.includes('listing') && (
        <FilterButtonMenu
          filterName={'Listings'}
          MenuItemsComponent={ListingFilterComponent}
          buttonStyles={buttonStyles}
          menuPaperProps={menuPaperProps}
          menuItemStyle={menuItemStyle}
          setFilterVal={setFilterVal}
          filterVal={filterVal}
          isMobileView={isMobileView}
          priceTiers={priceTiers}
          currency={currency}
          filterCount={totalListingFilterApplied}
          showFilterComingSoonAlert={showFilterComingSoonAlert}
          isUserAllowed={isUserAllowed}
        />
      )}
      {filtersToRender.includes('performance') && (
        <FilterButtonMenu
          filterName="Performance"
          MenuItemsComponent={PerformanceFilterComponent}
          buttonStyles={buttonStyles}
          menuPaperProps={menuPaperProps}
          menuItemStyle={menuItemStyle}
          setFilterVal={setFilterVal}
          filterVal={filterVal}
          isMobileView={isMobileView}
          priceTiers={priceTiers}
          currency={currency}
          filterCount={totalPerformanceFilterApplied}
          showFilterComingSoonAlert={showFilterComingSoonAlert}
          isUserAllowed={isUserAllowed}
        />
      )}
      {filtersToRender.includes('amenities') && (
        <FilterButtonMenu
          filterName="Amenities"
          MenuItemsComponent={AmenitiesFilterComponent}
          buttonStyles={buttonStyles}
          menuPaperProps={menuPaperProps}
          menuItemStyle={menuItemStyle}
          setFilterVal={setFilterVal}
          filterVal={filterVal}
          isMobileView={isMobileView}
          priceTiers={priceTiers}
          currency={currency}
          filterCount={totalAmenitiesFilterApplied}
          showFilterComingSoonAlert={showFilterComingSoonAlert}
          isUserAllowed={isUserAllowed}
        />
      )}
    </Box>
  );
};

export default FilterBar;
